import * as THREE from 'three';

import { track } from '../visits/bus';
import { watchContext } from './contextGuard';

export interface ContextLossDeps {
  renderer: THREE.WebGLRenderer;
  /** The element the banner is parented to, the sim's own #app. */
  app: HTMLElement;
  /** Re-arms the frame loop a loss stopped. */
  resume: () => void;
}

export interface ContextLossHandle {
  /** True while the context is gone, which is what stops the frame loop. */
  readonly lost: boolean;
  dispose(): void;
}

export function createContextLoss(deps: ContextLossDeps): ContextLossHandle {
  const { renderer, app } = deps;
  // ---------- Context loss ----------
  // Only three re-creates GL programs and buffers on restore; rebuilding them
  // by hand here would race that, so this just gates the frame loop and gives
  // the person a Reload rather than a frozen last frame.
  let contextLost = false;
  let contextLossBanner: HTMLElement | null = null;
  function showContextLossBanner(): void {
    if (contextLossBanner) return;
    const banner = document.createElement('div');
    banner.id = 'webgl-context-lost-banner';
    banner.className = 'panel';
    banner.style.cssText =
      'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);z-index:1000;' +
      'display:flex;flex-direction:column;align-items:center;gap:12px;text-align:center;padding:24px 32px;';
    const msg = document.createElement('div');
    msg.className = 'val';
    msg.textContent = 'The graphics context was lost; reload to continue.';
    const reload = document.createElement('button');
    reload.className = 'panel-btn';
    reload.textContent = 'Reload';
    reload.addEventListener('click', () => location.reload());
    banner.append(msg, reload);
    app.appendChild(banner);
    contextLossBanner = banner;
  }
  function hideContextLossBanner(): void {
    contextLossBanner?.remove();
    contextLossBanner = null;
  }
  const unwatch = watchContext(
    renderer,
    () => {
      contextLost = true;
      track('exception', { kind: 'context_lost', detail: 'WebGL context lost', fatal: true });
      showContextLossBanner();
    },
    () => {
      if (!contextLost) return; // a second restore without a loss would arm a second loop
      contextLost = false;
      hideContextLossBanner();
      requestAnimationFrame(deps.resume);
    },
  );

  return {
    get lost() { return contextLost; },
    dispose: () => {
      unwatch();
      hideContextLossBanner();
    },
  };
}
